import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mic, Loader2, Shield, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import logo from "@/assets/pk-logo.png";

export default function Login() {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login"); // login | register
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [mobile, setMobile] = useState("");
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setOk(""); setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password);
        nav("/");
      } else {
        const res = await register({ email, password, name, company_name: company, contact_mobile: mobile });
        setOk(res.message || "Registration received! Awaiting admin approval.");
        setMode("login");
      }
    } catch (e2) {
      setErr(apiErr(e2, mode === "login" ? "Login failed" : "Registration failed"));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-col justify-between w-1/2 bg-sidebar text-white p-12 relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-indigo-500/20 blur-3xl" />
        <div className="absolute bottom-0 -left-20 w-96 h-96 rounded-full bg-purple-500/20 blur-3xl" />
        <div className="flex items-center gap-3 relative">
          <img src={logo} alt="Parth Kalyani" className="h-16 w-auto" data-testid="app-logo" />
        </div>
        <div className="relative">
          <h1 className="text-4xl font-bold font-display leading-tight mb-4">Inbound + Outbound<br />AI calling for<br /><span className="text-indigo-400">B2B India.</span></h1>
          <p className="text-slate-400 max-w-md">Your own AI tele-caller qualifies every lead 24/7 — auto-dials enquiries and answers incoming calls in Hindi, Gujarati, Tamil & more, grounded on your own knowledge base.</p>
        </div>
        <a href="/admin/login" className="text-xs text-slate-400 hover:text-white flex items-center gap-1.5 relative w-fit" data-testid="admin-panel-link"><Shield className="w-3.5 h-3.5" /> Admin panel</a>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-3 mb-6">
            <img src={logo} alt="Parth Kalyani" className="h-12 w-auto" />
          </div>
          <div>
            <h2 className="text-2xl font-bold font-display">{mode === "login" ? "Welcome back" : "Create your account"}</h2>
            <p className="text-sm text-slate-500 mt-1">{mode === "login" ? "Sign in to your dashboard" : "Register your company — activated after admin approval"}</p>
          </div>

          {mode === "register" && (
            <>
              <div className="space-y-2"><Label>Company name</Label><Input value={company} onChange={(e) => setCompany(e.target.value)} data-testid="reg-company" required /></div>
              <div className="space-y-2"><Label>Your name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="reg-name" required /></div>
              <div className="space-y-2"><Label>Mobile</Label><Input value={mobile} onChange={(e) => setMobile(e.target.value)} data-testid="reg-mobile" placeholder="+91..." /></div>
            </>
          )}
          <div className="space-y-2">
            <Label>Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" required />
          </div>
          <div className="space-y-2">
            <Label>Password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" required />
          </div>
          {err && <div className="text-sm text-red-500" data-testid="login-error">{err}</div>}
          {ok && <div className="text-sm text-emerald-600 flex items-start gap-1.5" data-testid="register-success"><CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />{ok}</div>}
          <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === "login" ? "Sign In" : "Create Account")}
          </Button>
          <p className="text-sm text-center text-slate-500">
            {mode === "login" ? (
              <>New client? <button type="button" onClick={() => { setMode("register"); setErr(""); }} className="text-indigo-600 font-medium" data-testid="switch-register">Register your company</button></>
            ) : (
              <>Already have an account? <button type="button" onClick={() => { setMode("login"); setErr(""); }} className="text-indigo-600 font-medium" data-testid="switch-login">Sign in</button></>
            )}
          </p>
          {mode === "login" && <p className="text-xs text-slate-400 text-center">Demo client: anton@antonautomation.com / client123</p>}
        </form>
      </div>
    </div>
  );
}
